import { useState } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const money = (v) => `$${Number(v || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const LABELS = { net_dd: "Net pay — direct deposit", cra: "CRA remittance (PD7A)", wsib: "WSIB premium", eht: "Employer Health Tax", other: "Third-party deductions" };
const inp = "border border-input bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-black";

export function FundingModeControl({ company, isMaster, onCompanyChange }) {
  const [mode, setMode] = useState(company?.funding_mode || "net_pay");
  const [eht, setEht] = useState(company?.eht_rate ?? 0);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/companies/${company.company_id}/payments/funding-mode`, { funding_mode: mode, eht_rate: Number(eht) || 0 });
      toast.success(mode === "full_payroll" ? "Full payroll funding enabled" : "Net pay funding only"); onCompanyChange?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const full = (company?.funding_mode || "net_pay") === "full_payroll";
  return (
    <div className="text-sm mb-4 flex flex-wrap items-center gap-3" data-testid="dd-funding-mode">
      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Funding option</span>
      {isMaster ? (
        <>
          <select data-testid="dd-funding-mode-select" value={mode} onChange={(e) => setMode(e.target.value)} className={inp}>
            <option value="net_pay">Net pay only (client remits CRA/WSIB)</option>
            <option value="full_payroll">Full payroll funding (PostWage remits CRA, WSIB, EHT, third parties)</option>
          </select>
          {mode === "full_payroll" && <label className="text-xs flex items-center gap-1">EHT % <input data-testid="dd-eht-rate" type="number" step="0.01" min="0" max="5" value={eht} onChange={(e) => setEht(e.target.value)} className={`${inp} w-20`} /></label>}
          <button data-testid="dd-funding-mode-save" onClick={save} disabled={busy} className="text-xs border border-black px-3 py-1 hover:bg-black hover:text-white transition-colors disabled:opacity-50">Save</button>
        </>
      ) : <span>{full ? "Full payroll funding" : "Net pay only"}</span>}
    </div>
  );
}

export function FundingBreakdown({ preview, clientView }) {
  const bd = preview?.breakdown;
  if (!bd) return null;
  const hint = clientView ? "" : preview.postwage_last4 ? ` · remittances deposited to PostWage ····${preview.postwage_last4}` : " · PostWage settlement account not set (Settings → Payments)";
  return (
    <div className="border border-border bg-neutral-50/60 p-3 mb-3 text-sm" data-testid="dd-breakdown">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Full payroll funding — this run{hint}</div>
      {Object.entries(bd).filter(([, v]) => v > 0).map(([k, v]) => (
        <div key={k} className="flex justify-between py-0.5" data-testid={`dd-bd-${k}`}><span>{LABELS[k]}</span><span className="tabular-nums">{money(v)}</span></div>
      ))}
      <div className="flex justify-between border-t border-black mt-1 pt-1 font-semibold" data-testid="dd-bd-total"><span>Total debit from client</span><span className="tabular-nums">{money(preview.total_debit)}</span></div>
      {preview.funding_now !== preview.total_debit && <div className="text-xs text-muted-foreground mt-1">Debiting now: {money(preview.funding_now)} (remainder already funded)</div>}
    </div>
  );
}

export function RemittanceHolds({ companyId, holds, isMaster, onChange, clientView }) {
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ remitted_on: new Date().toISOString().slice(0, 10), confirmation: "" });
  if (!holds?.length) return null;
  const mark = async (id) => {
    try {
      await api.post(`/companies/${companyId}/payments/${id}/remitted`, form);
      toast.success("Marked remitted"); setOpen(null); onChange?.();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mt-4" data-testid="dd-holds">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Held for remittance by PostWage</div>
      <table className="w-full text-sm">
        <tbody>
          {holds.map((h) => (
            <tr key={h.payment_id} className="border-t border-border" data-testid={`dd-hold-${h.hold_type}`}>
              <td className="py-1.5">{h.employee_name}{h.deposit_to && !clientView ? <span className="text-xs text-muted-foreground"> · {h.deposit_to}</span> : null}</td>
              <td className="py-1.5 text-right tabular-nums">{money(h.amount)}</td>
              <td className="py-1.5 pl-4 text-xs">
                {h.status === "remitted" ? <span className="inline-flex items-center gap-1 text-emerald-700" data-testid={`dd-hold-remitted-${h.hold_type}`}><CheckCircle2 className="h-3.5 w-3.5" /> Remitted {h.remitted_on}{h.confirmation ? ` · #${h.confirmation}` : ""}</span>
                  : <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5">Held</span>}
              </td>
              <td className="py-1.5 pl-4 text-right">
                {isMaster && h.status === "held" && (open === h.payment_id ? (
                  <span className="inline-flex gap-1 items-center">
                    <input data-testid="dd-remit-date" type="date" value={form.remitted_on} onChange={(e) => setForm({ ...form, remitted_on: e.target.value })} className={inp} />
                    <input data-testid="dd-remit-conf" placeholder="Confirmation #" value={form.confirmation} onChange={(e) => setForm({ ...form, confirmation: e.target.value })} className={`${inp} w-32`} />
                    <button data-testid="dd-remit-save" onClick={() => mark(h.payment_id)} className="text-xs bg-black text-white px-2 py-1">Save</button>
                    <button data-testid="dd-remit-cancel" onClick={() => setOpen(null)} className="text-xs px-2 py-1">Cancel</button>
                  </span>
                ) : <button data-testid={`dd-mark-remitted-${h.hold_type}`} onClick={() => setOpen(h.payment_id)} className="text-xs border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors">Mark remitted</button>)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
